/**
 * Locale-specific utilities for tax ID formatting, normalization, and validation.
 * Currently supports:
 * - Brazil (BR): CNPJ
 * - Chile (CL): RUT
 * - Colombia (CO): NIT
 * - Mexico (MX): RFC
 * Each country has its own formatting and validation rules, which are implemented
 * in the respective normalizer, formatter, and validator functions.
 * The main exported functions are:
 * - normalizeTaxId(country, value): Normalizes the tax ID for the specified country.
 * - formatTaxId(country, value): Formats the tax ID for display according to the country's conventions.
 * - validateTaxId(country, value): Validates the tax ID against the country's rules.
 * The module is designed to be extensible, allowing for additional countries and tax ID formats to be added as needed.
 */

const formatThousands = (base) => base.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

const normalizeClTaxId = (value) => {
  const compactValue = value
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(/-/g, '')
    .toUpperCase()
    .replace(/[^0-9K]/g, '')

  if (!compactValue) {
    return ''
  }

  if (compactValue.length === 1) {
    return compactValue
  }

  return `${compactValue.slice(0, -1)}-${compactValue.slice(-1)}`
}

const normalizeCoTaxId = (value) => {
  const compactValue = value.replace(/\s/g, '').replace(/\./g, '')

  if (!compactValue) {
    return ''
  }

  const sanitizedValue = compactValue.replace(/[^\d-]/g, '')

  if (!sanitizedValue) {
    return ''
  }

  if (sanitizedValue.includes('-')) {
    const [basePart = '', ...checkDigitParts] = sanitizedValue.split('-')
    const rawCheckDigitPart = checkDigitParts.join('')
    const base = basePart.replace(/\D/g, '')
    const checkDigits = rawCheckDigitPart.replace(/\D/g, '')

    if (!base) {
      return ''
    }

    if (!checkDigits) {
      return base
    }

    if (checkDigits.length === 1) {
      return `${base}-${checkDigits}`
    }

    // If users keep typing at the end of a masked value (e.g. 8-03),
    // preserve all digits by moving extras back to the base and keeping
    // the last one as check digit.
    const expandedBase = `${base}${checkDigits.slice(0, -1)}`
    const checkDigit = checkDigits.slice(-1)

    return `${expandedBase}-${checkDigit}`
  }

  const digits = sanitizedValue.replace(/\D/g, '')

  if (digits.length <= 1) {
    return digits
  }

  return `${digits.slice(0, -1)}-${digits.slice(-1)}`
}

const normalizers = {
  CL: normalizeClTaxId,
  CO: normalizeCoTaxId
}

const formatClTaxId = (value) => {
  const normalizedValue = normalizeClTaxId(value)

  if (!normalizedValue) {
    return ''
  }

  const [base, checkDigit] = normalizedValue.split('-')
  const formattedBase = formatThousands(base)

  return typeof checkDigit === 'undefined'
    ? formattedBase
    : `${formattedBase}-${checkDigit}`
}

const formatCoTaxId = (value) => {
  const normalizedValue = normalizeCoTaxId(value)

  if (!normalizedValue) {
    return ''
  }

  const [base, checkDigit] = normalizedValue.split('-')
  const formattedBase = formatThousands(base)

  return typeof checkDigit === 'undefined'
    ? formattedBase
    : `${formattedBase}-${checkDigit}`
}

const formatters = {
  CL: formatClTaxId,
  CO: formatCoTaxId
}

export const normalizeTaxId = (country, value) => {
  if (typeof value !== 'string') {
    return value
  }

  const trimmedValue = value.trim()
  const normalizer = normalizers[country]

  if (!normalizer) {
    return trimmedValue
  }

  return normalizer(trimmedValue)
}

export const formatTaxId = (country, value) => {
  if (typeof value !== 'string') {
    return value
  }

  const formatter = formatters[country]

  if (!formatter) {
    return value
  }

  return formatter(value)
}

const validators = {
  BR: {
    regex: /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/,
    validate: (value) => {
      if (typeof value !== 'string') {
        return false
      }

      if (!validators.BR.regex.test(value)) {
        return false
      }

      return true
    }
  },
  CL: {
    regex: /[A-Za-z]/,
    validate: (value) => {
      if (typeof value !== 'string') {
        return false
      }

      if (validators.CL.regex.test(value.replace(/[kK]/g, ''))) {
        return false
      }

      const rut = normalizeTaxId('CL', value).replace(/\./g, '').replace(/-/g, '')

      if (rut.length <= 1) {
        return false
      }

      let digits = parseInt(rut.slice(0, -1), 10)
      let m = 0
      let s = 1

      while (digits > 0) {
        s = (s + digits % 10 * (9 - m++ % 6)) % 11
        digits = Math.floor(digits / 10)
      }

      return (((s > 0) ? String((s - 1)) : 'K') === rut.slice(-1))
    }
  },
  MX: {
    regex: /^([A-Z&Ñ]{3}[0-9]{6}[0-9A-Z]{3})$/,
    validate: (value) => {
      // check type
      if (typeof value !== 'string') {
        return false
      }

      // check length
      if (value.length !== 12) {
        return false
      }

      // check regex
      if (!validators.MX.regex.test(value)) {
        return false
      }

      // check format based into date
      const rfc = value.substring(3, 9)
      const year = parseInt(rfc.substring(0, 2), 10)
      const month = parseInt(rfc.substring(2, 4), 10)
      const day = parseInt(rfc.substring(4, 6), 10)
      const date = new Date(year + 2000, month - 1, day)

      if (!date) {
        return false
      }

      // check digit
      const _alphabet = '0123456789ABCDEFGHIJKLMN&OPQRSTUVWXYZ Ñ'
      const chars = '   ' + value
      const digit = chars.substring((chars.length - 2) - 11, (chars.length - 2) + 1)

      let sum = 0

      for (let i = 0; i < digit.length; i++) {
        const character = digit.charAt(i)
        const index = _alphabet.indexOf(character)
        sum += index * (13 - i)
      }

      if (parseInt(value.slice(-1)) !== parseInt(_alphabet[(11 - (sum % 11)) % 11])) {
        return false
      }

      return true
    }
  },
  CO: {
    regex: /^\d{1,15}-\d$/,
    validate: (value) => {
      if (typeof value !== 'string') {
        return false
      }

      const normalizedValue = normalizeTaxId('CO', value)
      if (!validators.CO.regex.test(normalizedValue)) {
        return false
      }

      const [base, providedCheckDigit] = normalizedValue.split('-')

      const weights = [71, 67, 59, 53, 47, 43, 41, 37, 29, 23, 19, 17, 13, 7, 3]
      const paddedBase = base.padStart(15, '0')
      let sum = 0

      for (let i = 0; i < paddedBase.length; i++) {
        sum += parseInt(paddedBase[i], 10) * weights[i]
      }

      const remainder = sum % 11
      const expectedCheckDigit = remainder > 1 ? 11 - remainder : remainder

      return expectedCheckDigit === parseInt(providedCheckDigit, 10)
    }
  }
}

export const validateTaxId = (country, value) => {
  const validator = validators[country]
  if (!validator || typeof validator.validate !== 'function') {
    return false
  }

  return validator.validate(normalizeTaxId(country, value))
}

export default {
  formatTaxId,
  normalizeTaxId,
  validators,
  validateTaxId
}
